import React, {
  RefObject,
} from 'react';
import isNil from 'lodash/isNil';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  ExternalLink,
  IconContainer,
  NumberedProcessItem,
  PrimaryButton,
  UnavailableLink,
} from 'views/EMR/components/PCC/Walkthrough/components/ContentPrimitives';
import {
  formatTemplate,
  walkthroughContent,
} from 'views/EMR/components/PCC/Walkthrough/content';
import type {
  SuccessPageDefinition,
} from 'views/EMR/components/PCC/Walkthrough/types';

type Props = {
  page: SuccessPageDefinition;
  headingRef: RefObject<HTMLHeadingElement | null>;
  bodyRef?: RefObject<HTMLDivElement | null>;
  organizationName: string;
  onReturnToVisitNote?: () => void;
  supportUrl?: string;
  onContactSupport?: () => void;
  onClose: () => void;
};

export default function SuccessPage({
  page,
  headingRef,
  bodyRef,
  organizationName,
  onReturnToVisitNote,
  supportUrl,
  onContactSupport,
  onClose,
}: Props) {
  const resolvedOrganizationName = notEmptyString(organizationName)
    ? organizationName
    : 'your organization';
  const heading = formatTemplate(page.headingTemplate, {
    organizationName: resolvedOrganizationName,
  });
  const description = formatTemplate(page.descriptionTemplate, {
    organizationName: resolvedOrganizationName,
  });
  const hasSupportUrl = notEmptyString(supportUrl);
  const hasSupportCallback = !isNil(onContactSupport);
  const hasReturnCallback = !isNil(onReturnToVisitNote);
  const primaryActionLabel = hasReturnCallback
    ? page.primaryActionLabel
    : 'Close';

  return (
    <div
      ref={bodyRef}
      className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-10 pt-14 sm:px-10 sm:pb-12 lg:px-[72px] lg:pr-20"
    >
      <div className="flex flex-1 flex-col justify-center gap-8">
        <IconContainer
          iconKey={page.iconKey}
          size="lg"
          variant="solid"
        />

        <div className="flex flex-col gap-3">
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="m-0 text-[32px] font-extrabold leading-[1.2] tracking-[-0.01em] text-[#0F1B2D] outline-none sm:text-[38px]"
          >
            {heading}
          </h1>
          <p className="m-0 max-w-[720px] text-lg leading-normal text-[#475569]">
            {description}
          </p>
        </div>

        <div className="flex flex-col gap-[18px] lg:flex-row">
          {page.steps.map((step, index) => (
            <NumberedProcessItem
              key={step.title}
              number={index + 1}
              title={step.title}
              description={step.description}
              variant="card"
              marker={step.marker}
            />
          ))}
        </div>

        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6">
          <PrimaryButton
            showArrow={hasReturnCallback}
            className="!h-[52px] !px-[26px]"
            onClick={hasReturnCallback ? onReturnToVisitNote : onClose}
          >
            {primaryActionLabel}
          </PrimaryButton>

          {hasSupportUrl ? (
            <ExternalLink
              href={supportUrl!}
              label={page.supportLinkLabel}
            />
          ) : null}
          {!hasSupportUrl && hasSupportCallback ? (
            <button
              type="button"
              onClick={onContactSupport}
              className="text-[15px] font-semibold text-brand-700 hover:text-brand-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
            >
              {page.supportLinkLabel}
            </button>
          ) : null}
          {!hasSupportUrl && !hasSupportCallback ? (
            <UnavailableLink
              label={page.supportLinkLabel}
              helperText={walkthroughContent.shared.supportUnavailableLabel}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
